import React, { useEffect, useState } from "react";
import { AlertTriangle, X, Loader2, Send, Clock } from "lucide-react";
import { TimeLeft } from "../TimeLeft";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

// Shown to a warned wolf: surfaces active warnings and lets them appeal to the Alphas.
export function WarningBanner() {
  const { user } = useAuth();
  const warnings = user?.warnings || 0;
  const timeoutUntil = user?.posting_timeout_until;
  const timedOut = timeoutUntil && new Date(timeoutUntil) > new Date();
  const [appeal, setAppeal] = useState(undefined); // undefined=loading, null=none, obj
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const dismissKey = `warnbanner_dismissed_${warnings}`;

  useEffect(() => {
    if (!user || warnings < 1) return;
    setDismissed(sessionStorage.getItem(dismissKey) === "1");
    api.get("/appeals/mine").then((r) => setAppeal(r.data)).catch(() => setAppeal(null));
  }, [user, warnings, dismissKey]);

  if (!user) return null;
  if (timedOut) {
    return (
      <div className="mb-4 rounded-xl border border-orange-500/30 bg-orange-950/30 px-4 py-3 flex items-start gap-3" data-testid="timeout-banner">
        <Clock className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
        <p className="text-sm text-orange-100">
          You're in a posting timeout — you can read the sanctuary but can't howl or comment. <span className="font-semibold" data-testid="timeout-countdown"><TimeLeft until={timeoutUntil} /></span> <span className="text-orange-200/70">(until {new Date(timeoutUntil).toLocaleString()})</span>.
        </p>
      </div>
    );
  }
  if (warnings < 1 || dismissed) return null;

  const submit = async () => {
    if (!msg.trim()) { toast.error("Write your appeal first."); return; }
    setSending(true);
    try {
      await api.post("/appeals", { message: msg });
      const r = await api.get("/appeals/mine");
      setAppeal(r.data);
      setOpen(false);
      setMsg("");
      toast.success("Appeal sent to the Alphas 🐺");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send appeal.");
    } finally {
      setSending(false);
    }
  };

  const info = appeal || {};
  const latest = info.latest;
  const status = latest && latest.status;
  const canAppeal = info.can_appeal;
  const left = info.appeals_left;
  const cooldownDate = info.cooldown_until ? new Date(info.cooldown_until).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
  const close = () => { sessionStorage.setItem(dismissKey, "1"); setDismissed(true); };
  const wc = `${warnings} active ${warnings === 1 ? "warning" : "warnings"}`;
  const leftLabel = typeof left === "number" ? `${left} ${left === 1 ? "appeal" : "appeals"} left` : "";

  return (
    <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-950/30 overflow-hidden" data-testid="warning-banner">
      <div className="flex items-start gap-3 px-4 py-3">
        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          {status === "open" ? (
            <p className="text-sm text-amber-100" data-testid="warning-banner-under-review">
              You have {wc} from the Alphas. <span className="font-semibold">Your appeal is under review.</span>
            </p>
          ) : status === "declined" && cooldownDate ? (
            <div data-testid="warning-banner-declined">
              <p className="text-sm text-amber-100">You have {wc}. <span className="font-semibold text-red-300">Your appeal was declined.</span></p>
              {latest.note && <p className="text-xs text-amber-200/80 mt-1 italic">Alpha note: "{latest.note}"</p>}
              <p className="text-xs text-amber-200/70 mt-1" data-testid="warning-appeal-cooldown">You can appeal again after {cooldownDate}. ({leftLabel})</p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-amber-100">You've received {wc} from the Alphas. Keep to the Code of the Pack.</p>
              {appeal !== undefined && (
                canAppeal ? (
                  !open && (
                    <button onClick={() => setOpen(true)} data-testid="warning-appeal-open" className="mt-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200 underline underline-offset-2">
                      Appeal a warning · {leftLabel}
                    </button>
                  )
                ) : (
                  <p className="mt-1.5 text-xs text-amber-200/70" data-testid="warning-appeal-none-left">No appeals remaining for these warnings.</p>
                )
              )}
            </div>
          )}
          {open && (
            <div className="mt-3 space-y-2" data-testid="warning-appeal-form">
              <textarea
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                rows={3}
                placeholder="Explain your side to the Alphas…"
                data-testid="warning-appeal-message"
                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-amber-500/50"
              />
              <div className="flex items-center gap-2">
                <button onClick={submit} disabled={sending} data-testid="warning-appeal-submit" className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs rounded-lg px-3 py-1.5 disabled:opacity-60">
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send appeal
                </button>
                <button onClick={() => setOpen(false)} className="text-xs text-slate-400 hover:text-slate-200">Cancel</button>
              </div>
            </div>
          )}
        </div>
        <button onClick={close} data-testid="warning-banner-dismiss" className="text-amber-500/70 hover:text-amber-300 shrink-0"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
